import { useEffect, useState } from "react";
import { megaApi, ezugiApi, casinoApi } from "./api";

// "Hub" = Ezugi catalogue (normalised to the same {games:[]} shape); cached + warmed exactly like MegaNodes.
// Live casino only: drop hub RNG slots (e.g. Altente) unless the name is clearly a live table; Aviatrix crash games stay (Mini Games).
const SLOT_CATS = new Set(["slot", "slots", "arcade", "virtual", "fishing", "video slots", "crash"]);
const TABLE_WORDS = /baccarat|roulette|blackjack|andar|bahar|teen ?patti|dragon tiger|sic ?bo|dice|poker|hold'?em|lucky 7|cricket war|hi-?lo|pok deng/i;
const isLiveTable = (g) => (g.category || "").toLowerCase() !== "sports" && /* BetBy sportsbook: only via the Sports page toggle */
  (g.provider_name === "Aviatrix" || !SLOT_CATS.has((g.category || "").toLowerCase()) || TABLE_WORDS.test(g.name || ""));
const shapeHub = (games) => games.filter(isLiveTable).sort((a, b) => (/^ezugi/i.test(a.provider_name) ? 0 : 1) - (/^ezugi/i.test(b.provider_name) ? 0 : 1));  // Ezugi first
const fetchCategory = (category) => (category === "Hub"
  ? ezugiApi.games().then((d) => ({ games: d.configured ? shapeHub(d.games) : [] }))
  : megaApi.games(category));

// Casino catalogue cache + image warm-up so lobbies render instantly instead of fetching on every mount.
// Stale-while-revalidate: peekGames() paints from any cached copy (up to STALE_MAX old) while getGames()
// refetches whenever the copy is older than FRESH, so new/removed games appear within a minute.
const FRESH = 60 * 1000;
const STALE_MAX = 24 * 60 * 60 * 1000;
const mem = {};
const inflight = {};
const warmed = new Set();

const entry = (category) => {
  if (mem[category]) return mem[category];
  try {
    const raw = sessionStorage.getItem(`mega:${category}`);
    if (raw) { mem[category] = JSON.parse(raw); return mem[category]; }
  } catch { /* storage unavailable */ }
  return null;
};

const read = (category, maxAge) => {
  const e = entry(category);
  return e && e.data && Date.now() - e.at < maxAge ? e.data : null;
};

const write = (category, data) => {
  mem[category] = { at: Date.now(), data };
  try { sessionStorage.setItem(`mega:${category}`, JSON.stringify(mem[category])); } catch { /* quota */ }
};

export const peekGames = (category) => read(category, STALE_MAX);

export const getGames = (category) => {
  const hit = read(category, FRESH);
  if (hit) return Promise.resolve(hit);
  if (!inflight[category]) {
    inflight[category] = fetchCategory(category)
      .then((d) => { write(category, d); return d; })
      .finally(() => { delete inflight[category]; });
  }
  return inflight[category];
};

// ONE request for all three lobbies (edge-cached by Cloudflare) — fills Slot/Live/Hub caches together so
// Home and Live Casino paint in a single pass instead of section by section.
let allInflight = null;
export const getAllGames = () => {
  const slot = read("Slot", FRESH), live = read("Live", FRESH), hub = read("Hub", FRESH);
  if (slot && live && hub) return Promise.resolve({ slot, live, hub });
  if (!allInflight) {
    allInflight = casinoApi.catalogue()
      .then((d) => {
        const hubGames = { games: d.hub?.configured ? shapeHub(d.hub.games) : [] };
        write("Slot", d.slot); write("Live", d.live); write("Hub", hubGames);
        return { slot: d.slot, live: d.live, hub: hubGames };
      })
      .catch(() => Promise.all([getGames("Slot"), getGames("Live"), getGames("Hub")]).then(([s, l, h]) => ({ slot: s, live: l, hub: h })))
      .finally(() => { allInflight = null; });
  }
  return allInflight;
};

// True once every lobby is cached; Home uses it to render all sections in one go.
export const useAllGamesReady = () => {
  const [ready, setReady] = useState(() => !!(peekGames("Slot") && peekGames("Live") && peekGames("Hub")));
  useEffect(() => { if (!ready) getAllGames().then(() => setReady(true)).catch(() => setReady(true)); }, [ready]);
  return ready;
};

export const warmImages = (games, limit = 24) => {
  games.slice(0, limit).forEach((g) => {
    if (!g.image || warmed.has(g.image)) return;
    warmed.add(g.image);
    const img = new Image();
    img.decoding = "async";
    img.src = g.image;
  });
};

// Called once on app boot: ONE combined request for all catalogues, then pre-decode the first screens of artwork.
export const warmCasino = () => {
  getAllGames().then(({ slot, live, hub }) => { warmImages(slot.games, 12); warmImages(live.games, 30); warmImages(hub.games, 30); }).catch(() => {});
};
